import type { ComponentProps } from "react";
import type { Icon, IconName, ListItemOption } from "@theorgchartdev/storybook";
import { enumBoxSubTypes, enumUIModes } from "@coreapp/constants";
import type { QChartBox } from "@coreapp/CoreApp/QChartBox";
import type { QPlanManager } from "@coreapp/CoreApp/QPlanManager";
import { kPlanBoxCommands } from "@coreapp/utils/ExecutePlanBoxCommand";
import {
  HasBoxFormatOverrides,
  type BoxFormatCommand
} from "@coreapp/utils/ExecuteBoxFormatCommand";
import type { GetDoc } from "@/Utils/StateLib";
import { getRecordOptions, isAgentRecord, type RecordOptionsPermissions } from "./getRecordOptions";
import { getReadOnlyBoxOptions } from "./getReadOnlyBoxOptions";
import {
  getEditEmployeeBoxOptions,
  getEditPositionBoxOptions,
  type EditBoxOptionGroups
} from "./getEditBoxOptions";
import { getPlanOptions } from "./getPlanOptions";

type ChartDoc = NonNullable<ReturnType<typeof GetDoc>>;

export type ToolbarOption = {
  label: string;
  value: string;
  icon: IconName;
  iconColor?: ComponentProps<typeof Icon>["color"];
  clickHandler?: () => void;
  dataTestId?: string;
  disabled?: boolean;
  // Trailing separator, in both the icon row and the overflow list.
  divider?: boolean;
  options?: ListItemOption[];
  listProps?: Record<string, unknown>;
};

export type BoxToolbarModel = { row: ToolbarOption[]; overflow: ToolbarOption[] };

export const kEmptyBoxToolbarModel: BoxToolbarModel = { row: [], overflow: [] };

export type GetBoxToolbarModelParams = {
  doc: ChartDoc;
  targetBox: QChartBox;
  isPosition: boolean;
  uiMode: number;
  readOnly: boolean;
  permissions: RecordOptionsPermissions & { canUpdatePlan: boolean; canAccessBoxLibrary: boolean };
  planMgr?: QPlanManager | null;
  onCommand: (cmd: string) => () => void;
  onBranchStyleChange: (target: { value: string }) => void;
  onFormatCommand: (cmd: BoxFormatCommand) => () => void;
  onAddToLibrary: () => void;
  wrapAction: (doAction: () => void) => () => void;
};

// Profile leads the row, split from the mode's actions; overflow groups are separated from each other.
function composeModel(
  lead: ToolbarOption | undefined,
  rowActions: ToolbarOption[],
  overflowGroups: ToolbarOption[][]
): BoxToolbarModel {
  const actions = rowActions.map(opt => ({ ...opt, divider: false }));
  const row = lead ? [{ ...lead, divider: actions.length > 0 }, ...actions] : actions;

  const groups = overflowGroups.filter(group => group.length > 0);
  const overflow = groups.flatMap((group, groupIdx) =>
    group.map((opt, i) => ({
      ...opt,
      divider: !!opt.divider || (i === group.length - 1 && groupIdx < groups.length - 1)
    }))
  );

  return { row, overflow };
}

function getEditGroups(
  params: GetBoxToolbarModelParams,
  isAgentBox: boolean
): EditBoxOptionGroups | null {
  const { doc, targetBox, isPosition, permissions, onCommand, onBranchStyleChange } = params;
  const handlers = {
    targetBox,
    onCommand,
    onBranchStyleChange,
    onFormatCommand: params.onFormatCommand,
    onAddToLibrary: permissions.canAccessBoxLibrary ? params.onAddToLibrary : null,
    hasFormatOverrides: HasBoxFormatOverrides(targetBox)
  };

  if (isPosition) {
    return doc.IsCustomChart() ? getEditPositionBoxOptions(handlers) : { structure: [], other: [] };
  }
  // Every other subtype (text, picture, legend, table…) keeps its background right-click menu.
  if (targetBox.GetBoxSubType() !== enumBoxSubTypes.chartBox) return null;

  return getEditEmployeeBoxOptions({
    ...handlers,
    isPositionDoc: doc.IsPositionDocument(),
    isAgentBox
  });
}

export function getBoxToolbarModel(params: GetBoxToolbarModelParams): BoxToolbarModel {
  const { doc, targetBox, isPosition, uiMode, readOnly, permissions, planMgr, onCommand } = params;
  const dataObj = targetBox.GetDataObj();
  if (!dataObj) return kEmptyBoxToolbarModel;

  const isAgentBox = isAgentRecord(dataObj);
  const recordOptions = getRecordOptions({
    isCustomChart: doc.IsCustomChart(),
    isPositionCustomChart: doc.IsCustomChart() && doc.IsPositionDocument(),
    readOnly,
    recordLabel: isPosition ? "Position" : "Employee",
    dataObj,
    permissions,
    onCommand
  });
  const profileOption = recordOptions.find(opt => opt.value === "doprofile");
  const detailOptions = recordOptions.filter(opt => opt.value !== "doprofile");

  if (uiMode === enumUIModes.edit && !readOnly) {
    const editGroups = getEditGroups(params, isAgentBox);
    if (!editGroups) return kEmptyBoxToolbarModel;
    return composeModel(profileOption, editGroups.structure, [detailOptions, ...editGroups.other]);
  }

  // Position containers never offered the view actions (make top, reload photo).
  const readOnlyOptions = isPosition ? [] : getReadOnlyBoxOptions({ doc, targetBox, onCommand });

  const canEditPlan =
    uiMode === enumUIModes.planning && !!planMgr && doc.IsPlanChart() && permissions.canUpdatePlan;
  if (canEditPlan) {
    const isPositionDoc = doc.IsPositionDocument();
    const planGroups = getPlanOptions({
      dataObj,
      planMgr,
      isPositionDoc,
      isPositionBox: isPosition,
      wrapAction: params.wrapAction
    });
    const planRow = isAgentBox
      ? planGroups.row.filter(opt => opt.value !== kPlanBoxCommands.addSub)
      : planGroups.row;
    if (planGroups.exclusive) return composeModel(undefined, planRow, [planGroups.overflow]);

    return composeModel(profileOption, planRow, [
      detailOptions,
      isPositionDoc ? [] : readOnlyOptions,
      planGroups.overflow
    ]);
  }

  return composeModel(profileOption, detailOptions, [readOnlyOptions]);
}
